import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { QueryKey } from '../../utils/api';
import { isServer } from '../../utils/utils';
import { bffPut } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { PageSource } from '../../types/page-source';
import type { HttpParams } from '../../utils/url-utils';
import type { Basket, OrderAddress } from './model';

interface UpdateShippingProps {
    shipmentId: string;
    address: OrderAddress;
    useAsBilling?: boolean;
}

export const useUpdateShippingAddressForShipment = (
    pageSource?: PageSource,
): UseMutationResult<Basket, unknown, UpdateShippingProps> => {
    const { data } = useBasket();
    const authContext = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ shipmentId, address, useAsBilling = false }: UpdateShippingProps) => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();

            const httpParams: HttpParams = [{ useAsBilling }];

            if (pageSource) {
                httpParams.push({ pageSource });
            }

            return bffPut(`/baskets/${data?.basketId}/shipments/${shipmentId}/shipping-address`, {
                basketCustomerId: data?.customerInfo?.customerId,
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                body: address,
                httpParams,
            });
        },
        onSuccess: async (basket): Promise<void> => {
            queryClient.setQueryData([BffQueryKey.BASKET], basket);

            await Promise.all([
                queryClient.invalidateQueries([BffQueryKey.SHIPPING_METHODS], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.BASKET_AVAILABILITY], {
                    exact: false,
                }),
                queryClient.invalidateQueries([QueryKey.USER_ADDRESS_ZIP_CODE], {
                    exact: false,
                }),
            ]);
        },
    });
};
